import React, { useState } from 'react';
import { sheetsClient } from '../../data/sheetsClient';
import { useTeacherData } from '../../context/TeacherDataContext';
import { Card, Button, Skeleton } from './UI';

// A single school-scoped, reusable link a teacher can hand to student
// choreographers so they can submit an entry themselves — it lands as
// Pending Review in the entry library for the teacher to approve/edit. Same
// "share this link" pattern as the dancer invite link on the Dancers page,
// plus a regenerate/revoke control since this one grants write access
// (submitting an entry) rather than just a self-registration form.
export default function ChoreographerLinkPanel({ onClose }) {
  const { choreographerLink, loading, error, refetch } = useTeacherData();
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = choreographerLink?.token
    ? `${window.location.origin}${process.env.PUBLIC_URL}/registration/choreographer-entry?token=${encodeURIComponent(choreographerLink.token)}`
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate this link? The current link will stop working immediately — anyone still using it will need the new one.')) return;
    setRegenerating(true);
    try {
      await sheetsClient.regenerateChoreographerLink();
      await refetch.choreographerLink();
    } catch (err) {
      alert(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Card
      title="Choreographer submission link"
      subtitle="Share this with your student choreographers — they can submit an entry without a login. It lands here as Pending Review for you to approve or edit."
      action={<button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', color: '#666' }}>✕</button>}
    >
      {loading.choreographerLink ? (
        <Skeleton height={38} radius={8} />
      ) : error.choreographerLink ? (
        <span style={{ fontSize: 13, color: '#C0392B' }}>Couldn't load your link: {error.choreographerLink.message}</span>
      ) : (
        <>
          <div style={{ background: '#F5F5F5', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#2D2E33', fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {url}
            </span>
            <Button size="sm" onClick={handleCopy}>{copied ? 'Copied ✓' : 'Copy'}</Button>
          </div>
          <div style={{ marginTop: 10 }}>
            <Button size="sm" onClick={handleRegenerate} disabled={regenerating}>
              {regenerating ? 'Regenerating…' : 'Regenerate link'}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
